import React, { useEffect, useState } from "react";
import ProductForm from "./ProductForm";
import Toast from "../../../common/Toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { BeerFormStateEdit } from "../../../constants/types";
import { productEndPoints } from "../../../constants/endpoints";
import { fetchProductData } from "../../../api/productDataAPI";
import { HeaderContainer, HeaderTitle } from "./ProductCreate.styles";
import { useBrandConfig } from "../../../queries/shared";
import { useUpdateProductMutation } from "../../../queries/product";
import { useAppDispatch } from "../../../redux/hooks";
import { setLoading } from "../../../redux/features/slices/appSlice";

const EditProduct: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const userData = location.state?.userData || {};
  const { id } = useParams();

  const { data: brandConfig } = useBrandConfig();
  const { mutate: updateProduct } = useUpdateProductMutation();

  const [error, setError] = useState(false);
  const [message, setMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  const [formData, setFormData] = useState<BeerFormStateEdit>({
    beerName: "",
    quantity: "",
    description: "",
    price: "",
    basePrice: 0,
    ed: 0,
    fileUpload: [],
    existingImages: [],
    abv: "",
    ibu: "",
    quantity_20L: 0,
    quantity_30L: 0,
    quantity_50L: 0,
    preorder: false,
    displayInHome: false,
  });

  useEffect(() => {
    const fetchProductDataAsync = async () => {
      try {
        const { getProducts } = productEndPoints;

        const { prebook = [], products = [] } = brandConfig ?? {};

        console.log("Fetching product data for id:", id);
        const productData = await fetchProductData(id, getProducts);
        console.log("Product data received:", productData);

        if (!productData) {
          console.error("No product data found");
          return;
        }

        const productId = String(productData?.id);

        const isProductMatch = (item: any) => String(item?.id) === productId;

        const isInPrebook = prebook.some(isProductMatch);

        const isInProductsWithBuyButton = products.some(
          (item: any) =>
            String(item?.id) === productId && item?.showBuyButton === true
        );

        const isDisplayed = isInPrebook || isInProductsWithBuyButton;

        const newFormData = {
          beerName: productData?.name || "",

          description: productData?.description || "",

          price: productData?.priceInCents
            ? String(Number(productData.priceInCents) / 100)
            : "",

          basePrice: Number(productData?.basePrice) || 0,

          ed: Number(productData?.ed) || 0,

          abv: String(productData?.abv || ""),

          ibu: String(productData?.ibu || ""),

          quantity: String(productData?.quantity_30L || ""),

          quantity_20L: Number(productData?.quantity_20L) || 0,

          quantity_30L: Number(productData?.quantity_30L) || 0,

          quantity_50L: Number(productData?.quantity_50L) || 0,

          preorder: Boolean(productData?.isPreorder),

          displayInHome: isDisplayed,

          fileUpload: [],

          existingImages: productData?.images || [],
        };

        console.log("Setting form data:", newFormData);
        setFormData(newFormData);
      } catch (error) {
        console.error("Error fetching or processing product data:", error);
      }
    };

    if (id) {
      fetchProductDataAsync();
    }
  }, [id, brandConfig]);

  const handleSubmit = (data: BeerFormStateEdit) => {
    setError(false);

    // Turn the global loader on at submit time and keep it on through the
    // upload, API call, cache invalidation, success toast and route transition
    // so the user sees a single, continuous loader.
    dispatch(setLoading(true));

    updateProduct(
      {
        data,
        id: id ?? "",
      },
      {
        onSuccess: (response: any) => {
          const success = response?.message === "Product updated successfully";

          if (success) {
            setMessage("Product details have been updated successfully");

            setShowToast(true);

            setTimeout(() => {
              navigate(`/maltora-seller/products`);
              dispatch(setLoading(false));
            }, 3000);
          } else {
            setError(true);

            setMessage(
              "Unable to update product details. Please check your input and try again"
            );

            setShowToast(true);
            dispatch(setLoading(false));
          }
        },

        onError: (err: any) => {
          setMessage(
            "Unable to update product details. Please check your input and try again"
          );

          setError(true);

          setShowToast(true);
          dispatch(setLoading(false));

          console.error("Error updating product:", err);
        },
      }
    );
  };

  return (
    <div>
      <HeaderContainer>
        <HeaderTitle variant="body1">Edit Product</HeaderTitle>
      </HeaderContainer>

      <Toast
        setShowToast={setShowToast}
        showToast={showToast}
        toastMessage={message}
        error={error}
      />

      <div>
        <ProductForm
          formTitle="Edit Product"
          beerName={formData.beerName}
          description={formData.description}
          price={formData.price}
          basePrice={formData.basePrice}
          ed={formData.ed}
          fileUpload={formData.fileUpload}
          quantity={formData.quantity}
          onSubmit={handleSubmit}
          existingImages={formData.existingImages}
          buttonTextOne="Save"
          buttonTextTwo="Reset"
          isEdit={true}
          abv={formData.abv}
          ibu={formData.ibu}
          preorder={formData.preorder}
          displayInHome={formData.displayInHome}
          quantity_20L={formData.quantity_20L}
          quantity_30L={formData.quantity_30L}
          quantity_50L={formData.quantity_50L}
        />
      </div>
    </div>
  );
};

export default EditProduct;
